<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';
import { login } from '../../api/auth';

const props = defineProps({
  ssoAuthToken: { type: String, default: '' },
  ssoAccountId: { type: String, default: '' },
  ssoConversationId: { type: String, default: '' },
  email: { type: String, default: '' },
  authError: { type: String, default: '' },
});

const router = useRouter();

const status = ref('signin');

const credentials = reactive({
  email: '',
  password: '',
  rememberMe: false,
});

const showPassword = ref(false);
const capsLockOn = ref(false);
const isLoading = ref(false);
const failedAttempts = ref(0);
const lastAttemptWarning = ref(false);

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MINUTES = 15;
const SUCCESS_REDIRECT_DELAY_MS = 1400;

const mfaToken = ref('');
const successRedirectURL = ref('');
const unlockAt = ref(null);
const tickNow = ref(Date.now());
let tickInterval = null;

const isEmailValid = computed(() =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)
);
const canSubmit = computed(
  () => isEmailValid.value && credentials.password.length >= 1 && !isLoading.value
);

const attemptsRemaining = computed(() =>
  Math.max(0, MAX_ATTEMPTS - failedAttempts.value)
);
const showAttemptsWarning = computed(
  () => failedAttempts.value >= 2 && failedAttempts.value < MAX_ATTEMPTS
);

const unlockTimeLabel = computed(() => {
  if (!unlockAt.value) return '';
  return new Date(unlockAt.value).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
});

const minutesUntilUnlock = computed(() => {
  if (!unlockAt.value) return 0;
  const diffMs = unlockAt.value - tickNow.value;
  return Math.max(0, Math.ceil(diffMs / 60000));
});

const onPasswordKey = event => {
  if (event.getModifierState) {
    capsLockOn.value = event.getModifierState('CapsLock');
  }
};

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const goToForgotPassword = () => {
  router.push({
    path: '/app/auth/reset/password',
    query: credentials.email ? { email: credentials.email } : {},
  });
};

const goToSignup = () => {
  router.push({ path: '/app/auth/signup' });
};

const handleSuccess = redirectURL => {
  successRedirectURL.value = redirectURL;
  status.value = 'success';
  setTimeout(() => {
    window.location = redirectURL || '/app/';
  }, SUCCESS_REDIRECT_DELAY_MS);
};

const handleLocked = () => {
  unlockAt.value = Date.now() + LOCK_DURATION_MINUTES * 60_000;
  tickNow.value = Date.now();
  if (tickInterval) clearInterval(tickInterval);
  // Tick every 5s so the countdown stays roughly accurate and we transition
  // back to the sign-in form within ~5s of the timeout expiring. Devise's
  // :time unlock_strategy auto-unlocks the backend account on the next
  // failed_attempts check, so the user gets fresh 5 attempts naturally.
  tickInterval = setInterval(() => {
    tickNow.value = Date.now();
    if (Date.now() >= unlockAt.value) {
      clearInterval(tickInterval);
      tickInterval = null;
      retryAfterLock();
    }
  }, 5_000);
  status.value = 'locked';
};

const submitLogin = async () => {
  if (!canSubmit.value) return;
  isLoading.value = true;
  lastAttemptWarning.value = false;
  try {
    const result = await login({
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe,
      autoRedirect: false,
    });

    if (result?.mfaRequired) {
      mfaToken.value = result.mfaToken;
      status.value = 'mfa';
      return;
    }

    if (result?.blocked) {
      // Admin block → branded dead-end page (no dashboard access).
      // reason 'suspended' = whole agency, 'paused' = single user.
      const routeName =
        result.reason === 'suspended' ? 'account_suspended' : 'account_paused';
      router.push({ name: routeName, query: { role: result.role } });
      return;
    }

    if (result?.locked) {
      failedAttempts.value = MAX_ATTEMPTS;
      handleLocked();
      return;
    }

    if (result?.lastAttempt) {
      failedAttempts.value = Math.max(failedAttempts.value + 1, MAX_ATTEMPTS - 1);
      lastAttemptWarning.value = true;
      useAlert(result.message);
      return;
    }

    if (result?.success) {
      failedAttempts.value = 0;
      handleSuccess(result.redirectURL);
      return;
    }
  } catch (error) {
    failedAttempts.value += 1;
    // devise_token_auth doesn't surface a last_attempt_warning AND its lock
    // response only arrives on the NEXT attempt after the limit is hit
    // (try 6 once the lock landed on try 5). To match the prototype's UX
    // we proactively switch to the locked screen as soon as the client
    // counter hits MAX_ATTEMPTS — the backend has already locked the
    // account at this point, so the next API call would have shown locked
    // anyway. The lock-instructions email is already on its way.
    if (failedAttempts.value >= MAX_ATTEMPTS) {
      handleLocked();
      return;
    }
    useAlert(error?.message || 'The email or password you entered is incorrect.');
  } finally {
    isLoading.value = false;
  }
};

const handleMfaVerified = () => {
  failedAttempts.value = 0;
  handleSuccess('/app/');
};

const handleMfaCancel = () => {
  mfaToken.value = '';
  status.value = 'signin';
  credentials.password = '';
};

const retryAfterLock = () => {
  unlockAt.value = null;
  if (tickInterval) clearInterval(tickInterval);
  tickInterval = null;
  failedAttempts.value = 0;
  credentials.password = '';
  status.value = 'signin';
};

onMounted(() => {
  if (props.authError) {
    useAlert('Could not sign you in. Please try again.');
  }
  if (props.email && !credentials.email) {
    credentials.email = decodeURIComponent(props.email);
  }
});

onBeforeUnmount(() => {
  if (tickInterval) clearInterval(tickInterval);
});
</script>

<template>
  <div class="cc-login-page">
    <div class="cc-login-noise" />

    <header class="cc-login-header">
      <div class="cc-brand-mark">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="38"
          height="38"
          viewBox="0 0 96 92"
          class="cc-brand-icon"
        >
          <defs>
            <linearGradient id="goldgrad-lg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#d8b86a" />
              <stop offset="0.5" stop-color="#a8842f" />
              <stop offset="1" stop-color="#7a5a14" />
            </linearGradient>
            <clipPath id="clipover-lg">
              <rect x="38" y="44" width="20" height="22" />
            </clipPath>
          </defs>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(7,11)"
            fill="none"
            stroke="#0d9488"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(7,11)" fill="#0d9488">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <path
            d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 32 36 L 37 47 L 20 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
            transform="translate(41,30)"
            fill="none"
            stroke="url(#goldgrad-lg)"
            stroke-width="7"
            stroke-linejoin="round"
          />
          <g transform="translate(41,30)" fill="#a8842f">
            <circle cx="15" cy="18" r="2.4" />
            <circle cx="24" cy="18" r="2.4" />
            <circle cx="33" cy="18" r="2.4" />
          </g>
          <g clip-path="url(#clipover-lg)">
            <path
              d="M 11 0 L 33 0 A 11 11 0 0 1 44 11 L 44 25 A 11 11 0 0 1 33 36 L 24 36 L 7 47 L 12 36 L 11 36 A 11 11 0 0 1 0 25 L 0 11 A 11 11 0 0 1 11 0 Z"
              transform="translate(7,11)"
              fill="none"
              stroke="#0d9488"
              stroke-width="7"
              stroke-linejoin="round"
            />
          </g>
        </svg>
        <div class="cc-brand-text">
          <div class="cc-wordmark">
            <span class="cc-wordmark-part cc-wordmark-creator">Creator</span><span class="cc-wordmark-dash">-</span><span class="cc-wordmark-part cc-wordmark-chat">Chat</span><span class="cc-wordmark-tld">.com</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="90"
            height="2"
            viewBox="0 0 90 2"
            class="cc-brand-divider"
          >
            <defs>
              <linearGradient id="tealfade-lg" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#0d9488" stop-opacity="0" />
                <stop offset="20%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="80%" stop-color="#0d9488" stop-opacity="1" />
                <stop offset="100%" stop-color="#0d9488" stop-opacity="0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="90" height="2" fill="url(#tealfade-lg)" />
          </svg>
          <div class="cc-tagline">Conversations · Tailored</div>
        </div>
      </div>
    </header>

    <main class="cc-login-main">
      <!-- SIGN IN FORM -->
      <div v-if="status === 'signin'" class="cc-auth-stack">
        <div class="cc-auth-card">
          <h1 class="cc-h-display">Welcome back</h1>
          <p class="cc-sub">Sign in to your Creator-Chat workspace.</p>

          <form class="cc-form" @submit.prevent="submitLogin">
            <div class="cc-field">
              <label class="cc-label">
                Work email <span class="cc-req">*</span>
              </label>
              <input
                v-model="credentials.email"
                type="email"
                class="cc-input"
                placeholder="you@agency.com"
                autocomplete="username"
                autofocus
              />
            </div>

            <div class="cc-field">
              <label class="cc-label cc-label-row">
                <span>Password <span class="cc-req">*</span></span>
                <a href="#" class="cc-link-accent" @click.prevent="goToForgotPassword">
                  Forgot password?
                </a>
              </label>
              <div class="cc-pwd-wrap">
                <input
                  v-model="credentials.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="cc-input cc-input--pwd"
                  placeholder="••••••••"
                  autocomplete="current-password"
                  @keyup="onPasswordKey"
                  @keydown="onPasswordKey"
                />
                <button
                  type="button"
                  class="cc-pwd-toggle"
                  @click="togglePassword"
                >
                  {{ showPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="capsLockOn" class="cc-caps">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 8l-6 6h12l-6-6z" />
                  <path d="M6 18h12" />
                </svg>
                Caps Lock is on
              </div>
            </div>

            <label class="cc-checkbox-row">
              <input v-model="credentials.rememberMe" type="checkbox" />
              <span>Remember me for 30 days</span>
            </label>

            <div v-if="showAttemptsWarning" class="cc-attempts-warn">
              <strong>{{ attemptsRemaining }}</strong>
              attempts remaining before your account is temporarily locked.
            </div>

            <button
              type="submit"
              class="cc-btn-primary"
              :disabled="!canSubmit"
            >
              <template v-if="isLoading">
                Signing in…
              </template>
              <template v-else>
                Sign in
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </template>
            </button>
          </form>

          <p class="cc-security-line">
            Protected by industry-standard encryption.<br />
            Your password is hashed with bcrypt · sessions secured by JWT.
          </p>

          <div class="cc-card-foot">
            Don't have an account?
            <a href="#" class="cc-link-accent" @click.prevent="goToSignup">Register →</a>
          </div>
        </div>
      </div>

      <!-- ACCOUNT LOCKED -->
      <div v-else-if="status === 'locked'" class="cc-auth-stack">
        <div class="cc-auth-card cc-auth-card--centered">
          <div class="cc-status-icon cc-status-icon--danger">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h1 class="cc-h-display">Account temporarily locked</h1>
          <p class="cc-sub">
            Too many failed sign-in attempts. For your security, we've temporarily locked your account.
          </p>

          <div class="cc-locked-panel">
            <div class="cc-locked-line">
              <strong>Locked for: ~{{ minutesUntilUnlock }} min</strong>
            </div>
            <div class="cc-locked-line cc-locked-line--muted">
              You can try again at
              <span class="cc-mono">{{ unlockTimeLabel }}</span>
            </div>
            <div class="cc-locked-help">
              We've also sent an unlock link to your email — clicking it lets you in immediately.
            </div>
          </div>

          <button type="button" class="cc-btn-primary" @click="goToForgotPassword">
            Reset password instead
          </button>

          <div class="cc-card-foot cc-card-foot--row">
            <a href="mailto:support@creator-chat.com" class="cc-link-accent">Contact support</a>
            <a href="#" class="cc-link-accent" @click.prevent="retryAfterLock">Try again</a>
          </div>
        </div>
      </div>

      <!-- SIGN-IN SUCCESS -->
      <div v-else-if="status === 'success'" class="cc-auth-stack">
        <div class="cc-auth-card cc-auth-card--centered">
          <div class="cc-status-icon cc-status-icon--accent-solid">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 class="cc-h-display">Signed in successfully</h1>
          <p class="cc-sub">Redirecting you to your dashboard…</p>
          <div class="cc-success-spinner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0f766e" stroke-width="2.5" stroke-linecap="round" class="cc-spinner">
              <line x1="12" y1="2" x2="12" y2="6" />
              <line x1="12" y1="18" x2="12" y2="22" />
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
              <line x1="2" y1="12" x2="6" y2="12" />
              <line x1="18" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
            </svg>
          </div>
          <div class="cc-success-help">
            Taking too long?
            <a :href="successRedirectURL || '/app/'" class="cc-link-accent">Go to dashboard manually</a>
          </div>
        </div>
      </div>

      <!-- 2FA (fallback to existing component until #232b branded MFA lands) -->
      <div v-else-if="status === 'mfa'" class="cc-mfa-fallback">
        <MfaVerification
          :mfa-token="mfaToken"
          @verified="handleMfaVerified"
          @cancel="handleMfaCancel"
        />
      </div>
    </main>

    <footer class="cc-site-footer" role="contentinfo">
      <div class="cc-site-footer-links">
        <a href="https://creator-chat.com/#features" target="_blank" rel="noopener">Features</a>
        <a href="https://creator-chat.com/#fangate" target="_blank" rel="noopener">Fangate</a>
        <a href="https://creator-chat.com/#pricing" target="_blank" rel="noopener">Pricing</a>
        <a href="https://creator-chat.com/faq.html" target="_blank" rel="noopener">FAQ</a>
        <a href="mailto:info@creator-chat.com">Contact</a>
        <a href="https://creator-chat.com/imprint.html" target="_blank" rel="noopener">Imprint</a>
        <a href="https://creator-chat.com/privacy.html" target="_blank" rel="noopener">Privacy</a>
        <a href="https://creator-chat.com/terms.html" target="_blank" rel="noopener">Terms</a>
      </div>
      <div class="cc-site-footer-meta">
        <div class="cc-site-footer-email">
          <a href="mailto:info@creator-chat.com">info@creator-chat.com</a>
        </div>
        © 2026 Creator Chat. Built for creators who take their work seriously. Hosted in the European Union.
      </div>
    </footer>
  </div>
</template>

<style scoped>
.cc-login-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  background: #efeae0;
  color: #0b1414;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}
.cc-login-noise {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.35;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.4 0 0 0 0 0.35 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
}

.cc-login-header {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 36px;
}

.cc-brand-mark { display: flex; align-items: center; }
.cc-brand-icon { display: block; margin-right: 14px; }
.cc-brand-text { line-height: 1; }
.cc-wordmark { line-height: 21px; white-space: nowrap; }
.cc-wordmark-part {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  text-transform: uppercase;
  color: #0b1414;
}
.cc-wordmark-creator { font-weight: 400; }
.cc-wordmark-chat { font-weight: 300; }
.cc-wordmark-dash {
  font-family: 'Fraunces', 'Times New Roman', Georgia, serif;
  font-size: 21px;
  letter-spacing: 1.26px;
  color: #a8842f;
  font-weight: 400;
}
.cc-wordmark-tld {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: lowercase;
  color: #a8842f;
  vertical-align: 8px;
  padding-left: 2px;
}
.cc-brand-divider { display: block; margin: 6px 0 5px 0; }
.cc-tagline {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 8.5px;
  font-weight: 500;
  letter-spacing: 2.56px;
  color: rgba(11, 20, 20, 0.5);
  text-transform: uppercase;
  line-height: 1;
}

.cc-login-main {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 40px;
}

.cc-auth-stack {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
}

.cc-auth-card {
  background: #ffffff;
  border: 1px solid #e0d8c5;
  border-radius: 16px;
  padding: 38px 36px 32px;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 6px 24px rgba(11, 20, 20, 0.04);
}
.cc-auth-card--centered { text-align: center; }

.cc-h-display {
  font-family: 'Cinzel', 'Fraunces', 'Times New Roman', Georgia, serif;
  font-weight: 600;
  color: #0b1414;
  letter-spacing: -0.005em;
  font-size: 26px;
  line-height: 1.2;
  margin: 0 0 6px;
  text-align: center;
}
.cc-sub {
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  color: #6e7570;
  line-height: 1.5;
  margin: 0 0 28px;
  text-align: center;
}

.cc-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cc-field { display: flex; flex-direction: column; }
.cc-label {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #2a3434;
  margin-bottom: 6px;
}
.cc-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.cc-req { color: #dc2626; font-weight: 800; margin-left: 2px; }

.cc-input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #0b1414;
  padding: 11px 14px;
  border-radius: 10px;
  border: 1.5px solid #e0d8c5;
  background: #fdfcf9;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.cc-input:hover { border-color: #cfc5ac; }
.cc-input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.22);
}
.cc-input--pwd { padding-right: 70px; }

.cc-pwd-wrap { position: relative; }
.cc-pwd-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6e7570;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cc-pwd-toggle:hover { color: #0b1414; }

.cc-caps {
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(168, 132, 47, 0.1);
  border: 1px solid rgba(168, 132, 47, 0.3);
  padding: 6px 10px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: #7a5a14;
}

.cc-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #2a3434;
  margin-bottom: 6px;
}
.cc-checkbox-row input[type='checkbox'] {
  width: 16px;
  height: 16px;
  accent-color: #0d9488;
  cursor: pointer;
  margin: 0;
}

.cc-attempts-warn {
  background: rgba(168, 132, 47, 0.1);
  border: 1px solid rgba(168, 132, 47, 0.3);
  border-radius: 9px;
  padding: 10px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #7a5a14;
  line-height: 1.4;
}
.cc-attempts-warn strong { color: #5a4010; }

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 12px 20px;
  border-radius: 10px;
  border: 1.5px solid #0b1414;
  background: #0b1414;
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;
}
.cc-btn-primary:hover:not(:disabled) {
  background: #142020;
  border-color: #142020;
}
.cc-btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.cc-security-line {
  margin: 20px 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #6e7570;
  text-align: center;
  line-height: 1.45;
}

.cc-card-foot {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e0d8c5;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #6e7570;
}
.cc-card-foot--row {
  display: flex;
  justify-content: center;
  gap: 18px;
  font-size: 12.5px;
}
.cc-link-accent {
  color: #0f766e;
  font-weight: 600;
  text-decoration: none;
  font-size: 12px;
}
.cc-link-accent:hover { text-decoration: underline; }

.cc-status-icon {
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}
.cc-status-icon--danger {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}
.cc-status-icon--accent-solid {
  background: #0d9488;
  color: #ffffff;
}

.cc-locked-panel {
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.25);
  border-radius: 11px;
  padding: 14px 16px;
  text-align: left;
  margin-bottom: 22px;
}
.cc-locked-line {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #2a3434;
  line-height: 1.55;
}
.cc-locked-line strong { color: #dc2626; }
.cc-locked-line--muted {
  margin-top: 4px;
  font-size: 12.5px;
  color: #6e7570;
}
.cc-mono {
  font-family: 'JetBrains Mono', monospace;
  color: #0b1414;
  font-weight: 600;
}
.cc-locked-help {
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
  line-height: 1.5;
}

.cc-success-spinner {
  margin: 18px 0 14px;
  display: flex;
  justify-content: center;
}
.cc-spinner {
  animation: cc-spin 0.8s linear infinite;
}
@keyframes cc-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.cc-success-help {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6e7570;
}

.cc-mfa-fallback {
  width: 100%;
  max-width: 520px;
}

.cc-site-footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid #e0d8c5;
  margin-top: 64px;
  padding: 40px 36px 36px;
}
.cc-site-footer-links {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
.cc-site-footer-links a {
  color: #6e7570;
  text-decoration: none;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.15s;
}
.cc-site-footer-links a:hover { color: #0f766e; }
.cc-site-footer-meta {
  color: #6e7570;
  font-size: 12px;
  margin: 24px auto 0;
  max-width: 1100px;
  text-align: center;
  padding-top: 24px;
  border-top: 1px solid #e0d8c5;
  font-family: 'Inter', sans-serif;
}
.cc-site-footer-meta a {
  color: inherit;
  text-decoration: none;
  font-weight: 600;
}
.cc-site-footer-email { margin-bottom: 6px; }

@media (max-width: 540px) {
  .cc-login-header { padding: 18px 20px; }
  .cc-login-main { padding: 16px 16px 32px; }
  .cc-auth-card { padding: 30px 22px 26px; }
  .cc-site-footer { padding: 32px 20px 28px; margin-top: 48px; }
  .cc-site-footer-links { gap: 16px; }
  .cc-tagline { display: none; }
}
</style>
